/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  forwardRef, useContext, useEffect, useImperativeHandle, useState,
} from 'react';
import cn from 'classnames';
import useSWR from 'swr';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog, Zoom } from '@material-ui/core';
import { DeleteOutline as DeleteOutlineIcon } from '@material-ui/icons';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { ReactComponent as DragIcon } from 'Assets/drag.svg';
import AlterImage from 'Assets/alter-image.png';
import RotatingLoader from 'Components/RotatingLoader';
import { SquareCheckbox } from './CustomDesignsClassic';
import styles from '../../content-layout-details/index.module.css';
import modalStyles from './AlbumPhotosView.module.css';

const PhotoCard = ({
  photo, isDragging, isOverlay, isSelected, onToggleSelect,
}) => {
  const thumbnail = photo.imagekit_url || photo.image || AlterImage;
  const title = photo?.title || photo?.image?.split('/').pop() || '';
  const stopDrag = (e) => e.stopPropagation();
  return (
    <div
      className={cn({
        [styles.contentCard]: true,
        [styles.dragging]: isDragging,
        [styles.overlay]: isOverlay,
        [modalStyles.selectedCard]: isSelected,
      })}
    >
      <div className={styles.cardThumbnail}>
        <img
          src={thumbnail}
          alt={title}
          // `.cardThumbnail img` starts at opacity:0 and only shows once
          // data-loaded='true' is set — mark it on load (and for already-cached
          // images via ref), else the tiles stay blank white.
          ref={(el) => { if (el && el.complete) el.setAttribute('data-loaded', 'true'); }}
          onLoad={(e) => { e.currentTarget.dataset.loaded = 'true'; }}
          onError={(e) => { e.currentTarget.src = AlterImage; e.currentTarget.dataset.loaded = 'true'; }}
        />
        {!isOverlay && onToggleSelect && (
          <div
            className={modalStyles.checkboxWrap}
            onPointerDown={stopDrag}
            onMouseDown={stopDrag}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(photo);
            }}
          >
            <SquareCheckbox selected={isSelected} onClick={() => {}} />
          </div>
        )}
        <div className={styles.dragHandle}>
          <DragIcon />
        </div>
      </div>
      <div className={styles.cardInfo}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
    </div>
  );
};

const SortablePhoto = ({
  id, photo, isSelected, onToggleSelect,
}) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    minWidth: 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PhotoCard
        photo={photo}
        isDragging={isDragging}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
      />
    </div>
  );
};

const AlbumPhotosView = forwardRef(({
  album, sortBy, order, onLoadingChange,
  selectedPhotos = [], setSelectedPhotos,
}, ref) => {
  const { setAppSnackbar } = useContext(AppContext);
  const albumId = album?.content_id;

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // photosToDelete is an array — 1 element for per-card single delete,
  // N elements for bulk delete triggered from the right-side panel.
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPhotoSelected = (photoId) => selectedPhotos?.some((p) => p?.id === photoId);

  const togglePhotoSelection = (photo) => {
    if (!setSelectedPhotos) return;
    setSelectedPhotos((prev) => {
      const exists = prev?.some((p) => p?.id === photo?.id);
      if (exists) return prev.filter((p) => p?.id !== photo?.id);
      return [...(prev || []), photo];
    });
  };

  useImperativeHandle(ref, () => ({
    requestBulkDelete: () => {
      if (!selectedPhotos || selectedPhotos.length === 0) return;
      setPhotosToDelete(selectedPhotos);
    },
  }), [selectedPhotos]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Only Name sort sends keys; Layout Order is the default and sends none.
  const isNameSort = sortBy === 'name';

  const { isValidating: gettingAlbum, mutate: getAlbum } = useSWR(
    [endpoints.getMyAlbumDetail, albumId, isNameSort ? sortBy : null, isNameSort ? order : null],
    {
      fetcher: (url, album_id, sort_by, order_by) => {
        const payload = { album_id };
        if (sort_by) {
          payload.sort_by = sort_by;
          // Parent dropdown convention: order=1 → A-Z, order=0 → Z-A.
          // getMyAlbumDetail convention is inverted: order_by=0 → A-Z, order_by=1 → Z-A.
          payload.order_by = order_by === 1 ? 0 : 1;
        }
        return dataFetcher(url, payload);
      },
      onSuccess: ({ success, data }) => {
        if (success) {
          setItems(data?.[0]?.photos || []);
        } else {
          setAppSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Oops! Something went wrong',
          });
        }
      },
      onError: () => {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong',
        });
      },
    }
  );

  useEffect(() => {
    setItems([]);
    setActiveId(null);
    if (albumId) {
      getAlbum();
    }
  }, [albumId]);

  useEffect(() => {
    if (albumId) {
      getAlbum();
    }
  }, [sortBy, order]);

  // Mirror fetch state up to the parent so it can show/hide its loader.
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(Boolean(gettingAlbum));
    }
  }, [gettingAlbum]);

  // Clear parent loader on unmount in case we leave mid-fetch.
  useEffect(() => () => {
    if (onLoadingChange) onLoadingChange(false);
  }, []);

  const reorderOnServer = async (itemId, afterId) => {
    try {
      const response = await dataFetcher(endpoints.reorderItem, {
        type: 'album_photo',
        item_id: itemId,
        after_id: afterId,
        parent_id: albumId,
      });
      if (!response?.success) {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Reorder Album Photo',
        description: 'Album photo reorder failed',
        severity: 'medium',
        errorMessage: err?.message || 'Reorder failed',
        component: 'AlbumPhotosView',
        userAction: 'Drag end album photo reorder',
      });
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setPhotosToDelete([]);
  };

  const deleteCount = photosToDelete.length;
  const firstPhoto = photosToDelete[0];

  const handleConfirmDelete = async () => {
    const ids = photosToDelete.map((p) => p?.id).filter(Boolean);
    if (ids.length === 0) return;
    setIsDeleting(true);
    try {
      const response = await dataFetcher(endpoints.deleteMyPhoto, { photo_ids: ids });
      if (response?.success) {
        const idSet = new Set(ids);
        setItems((prev) => prev.filter((it) => !idSet.has(it.id)));
        if (setSelectedPhotos) {
          setSelectedPhotos((prev) => (prev || []).filter((p) => !idSet.has(p?.id)));
        }
        setAppSnackbar({
          isVisible: true,
          message: ids.length === 1 ? 'Photo Removed' : `${ids.length} Photos Removed`,
        });
        setPhotosToDelete([]);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong while removing photo',
        });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Delete Album Photo',
        description: 'Album photo delete failed',
        severity: 'medium',
        errorMessage: err?.message || 'Delete failed',
        component: 'AlbumPhotosView',
        userAction: 'Click delete on album photo',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Oops! Something went wrong while removing photo',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    const afterId = newIndex === 0 ? null : newItems[newIndex - 1]?.id;
    reorderOnServer(active.id, afterId);
  };

  const activePhoto = activeId ? items.find((it) => it.id === activeId) : null;

  if (gettingAlbum && items.length === 0) {
    return <RotatingLoader />;
  }

  if (!items || items.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontFamily: 'Montserrat, sans-serif' }}>
        No Photos in this album
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map((it) => it.id)} strategy={rectSortingStrategy}>
          <div className={styles.contentGrid} style={{ paddingLeft: 24, paddingRight: 24 }}>
            {items.map((photo) => (
              <SortablePhoto
                key={photo.id}
                id={photo.id}
                photo={photo}
                isSelected={isPhotoSelected(photo.id)}
                onToggleSelect={togglePhotoSelection}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
          {activePhoto ? <PhotoCard photo={activePhoto} isDragging isOverlay /> : null}
        </DragOverlay>
      </DndContext>

      <Dialog
        open={deleteCount > 0}
        TransitionComponent={Zoom}
        keepMounted
        aria-labelledby="delete-photo-dialog-title"
        aria-describedby="delete-photo-dialog-description"
        maxWidth="xs"
        fullWidth
        onClose={handleCancelDelete}
        PaperProps={{ className: modalStyles.dialogPaper }}
      >
        <div className={modalStyles.dialogContainer}>
          <div className={modalStyles.iconWrap}>
            <DeleteOutlineIcon />
          </div>
          <h3 id="delete-photo-dialog-title" className={modalStyles.dialogTitle}>
            {deleteCount > 1 ? `Delete ${deleteCount} Photos?` : 'Delete Photo?'}
          </h3>
          <p id="delete-photo-dialog-description" className={modalStyles.dialogMessage}>
            {deleteCount > 1
              ? `Are you sure you want to delete these ${deleteCount} photos?`
              : (firstPhoto?.title
                ? `Are you sure you want to delete "${firstPhoto.title}"?`
                : 'Are you sure you want to delete this photo?')}
            <br />
            This action cannot be undone.
          </p>
          <div className={modalStyles.actionContainer}>
            <button
              type="button"
              className={modalStyles.cancelBtn}
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={modalStyles.deleteBtn}
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting
                ? 'Deleting...'
                : (deleteCount > 1 ? `Delete ${deleteCount} Photos` : 'Delete')}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
});

AlbumPhotosView.displayName = 'AlbumPhotosView';

export default AlbumPhotosView;
